import { ITEMS_PER_PAGE_10, useSearchParams, capitalizeAll, PaginationBar, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, GenericMessageBox,
    BaseInfoCompProps } from '@moderepo/mode-app-components';
import React, { CSSProperties, useCallback, useContext, useMemo, useState } from 'react';
import { Alert } from '@moderepo/mode-internal-apis';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { UIFetchAlertsFilters, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useSelectAlerts, useSelectProject } from '../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { AlertsTable, AlertsTableDataItem, AlertsTableProps } from '../../components';
import { useRitzTranslation } from '../../utils/customHooks';
import { getAlertConditionTypeDisplayName, getAlertSeverityDisplayName, getAlertStateDisplayName } from '.';
import { AlertPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { getEntityDisplayName, RouteHelper } from '..';
import { getDateTimeDisplay } from '../containerUtils';



const useStyle = makeStyles(() => {
    return {
        noDataMessageComp: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
            height        : '100%',
        },
    };
}, { name: 'AlertsListingContainer', index: 2 });



/**
 * The interface for the list of filters current selected.
 * TODO - Add more filters base on what the backend support.
 */
interface AlertsSearchFields {
    readonly entityClass: string | undefined
}



/**
 * By default, we will show all columns of the AlertsTable. However, if the container wants to hide some columns, it can specify the
 * set of column it want to hide and these are the columns that can be customized.
 */
export interface AlertsTableHiddenFields {
    readonly id?: boolean | undefined;
    readonly description?: boolean | undefined;
    readonly state?: boolean | undefined;
    readonly severity?: boolean | undefined;
    readonly conditionType?: boolean | undefined;
    readonly entity?: boolean | undefined;
    readonly recoveryTime?: boolean | undefined;
    readonly invocationTime?: boolean | undefined;
}


/**
 * We will allow the parent container to provide initial attributes for filtering alerts. However, there are some attributes that shouldn't
 * be provided by the parent container e.g. `pageNumber` and `pageSize` therefore we will define a new interface that extends the
 * UIFetchAlertsFilters but without the attributes we don't allow the parent container to specify.
 */
interface InitialAlertsFilters extends Omit<UIFetchAlertsFilters, 'pageNumber' | 'pageSize'> {
}


/**
 * This is the set of attributes required by the AlertsListingContainer
 */
export interface AlertsListingContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    readonly projectId: number;

    readonly title?: string | undefined;
    readonly icon?: string | undefined;
    readonly customSecondaryActions?: AlertsTableProps['customSecondaryActions'];
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
    } | undefined;

    // To allow the parent container to provide some filter to use initially
    readonly filters?: InitialAlertsFilters | undefined;

    // Customized page size value. If not provided, we will use 10 as default
    readonly itemsPerPage?: number | undefined;

    // Customized set of Alerts table fields the parent container wants to hide
    readonly hiddenFields?: AlertsTableHiddenFields | undefined;
}


/**
 * This is a container used for displaying lists of Alerts in tabular format.
 */
export const AlertsListingContainer: React.FC<AlertsListingContainerProps> = (props: AlertsListingContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const navigate = useNavigate();
    const classes = useStyle();



    // State for managing the pagination and filters
    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<AlertsSearchFields>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            entityClass: undefined,
        },
    });


    const filters = useMemo((): UIFetchAlertsFilters => {
        return {
            pageNumber: searchParams.currentPage,
            pageSize  : searchParams.itemsPerPage,
            ...props.filters,
        };
    }, [props.filters, searchParams.currentPage, searchParams.itemsPerPage]);
    const alerts = useSelectAlerts(props.projectId, filters);


    // The state to keep track of the selected alert.
    const [selectedAlert, setSelectedAlert] = useState<Alert | undefined>();


    /**
     * On alert selected, set the selected alert locally and also dispatch an action to show the Preview panel for the selected alert
     */
    const onPreviewClicked = useCallback((alert: Alert) => {
        setSelectedAlert(alert);

        // Dispatch an action to show the Preview Panel for the selected alert.
        const options: AlertPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.ALERT_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    alertId  : alert.id,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);



    /**
     * On alert selected, take the user to the investigate alert page
     */
    const onAlertSelected = useCallback((alert: Alert) => {
        RouteHelper.getInstance(navigate).gotoInvestigateAlertPage(props.projectId, alert.id);
    }, [props.projectId, navigate]);



    /**
     * Localize the current list of alerts object. This data will be passed to the AlertsTable component for display.
     */
    const localizedAlertList = useMemo((): readonly AlertsTableDataItem[] | undefined => {
        if (alerts) {
            return alerts.items.map((alert): AlertsTableDataItem => {
                return {
                    actualValue : alert,
                    selected    : selectedAlert?.id === alert.id,
                    displayValue: {
                        id           : alert.id.toString(),
                        state        : trans(getAlertStateDisplayName(alert.state)),
                        description  : alert.description,
                        entity       : getEntityDisplayName(props.projectId, alert.entityId, appDataState, appDataDispatch),
                        severity     : trans(getAlertSeverityDisplayName(alert.severity)),
                        conditionType: capitalizeAll(trans(
                            getAlertConditionTypeDisplayName(alert.condition.conditionType) ?? 'label.unknown',
                        )),
                        invocationTime: getDateTimeDisplay(alert.invocationTime, trans),
                        recoveryTime  : alert.recoveryTime
                            ? getDateTimeDisplay(alert.recoveryTime, trans)
                            : undefined,

                    },
                };
            });
        }
        return undefined;
    }, [alerts, appDataDispatch, appDataState, props.projectId, selectedAlert?.id, trans]);



    /**
     * Set up the Alerts table's fields and cache it. We will pass this settings to the AlertsTable.
     */
    const alertsTableFieldsSettings = useMemo((): AlertsTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label: '',
                },
                id: {
                    label : capitalizeAll(trans('label.id')),
                    hidden: props.hiddenFields?.id,
                },
                severity: {
                    label : capitalizeAll(trans('label.severity')),
                    hidden: props.hiddenFields?.severity,
                },
                state: {
                    label : capitalizeAll(trans('label.state')),
                    hidden: props.hiddenFields?.state,
                },
                entity: {
                    label : capitalizeAll(trans('field.entity.label')),
                    hidden: props.hiddenFields?.entity,
                },
                description: {
                    label : capitalizeAll(trans('label.description')),
                    hidden: props.hiddenFields?.description,
                },
                invocationTime: {
                    label : capitalizeAll(trans('alerting.label.invocation_date')),
                    hidden: props.hiddenFields?.invocationTime,
                },
                recoveryTime: {
                    label : capitalizeAll(trans('alerting.label.recovered_date')),
                    hidden: props.hiddenFields?.recoveryTime,
                },
                conditionType: {
                    label : capitalizeAll(trans('alerting.alert_condition_type')),
                    hidden: props.hiddenFields?.conditionType,
                },
            },
        };
    }, [props.hiddenFields?.conditionType, props.hiddenFields?.description, props.hiddenFields?.entity, props.hiddenFields?.id,
        props.hiddenFields?.invocationTime, props.hiddenFields?.recoveryTime, props.hiddenFields?.severity, props.hiddenFields?.state,
        trans]);


    // Create PaginationBar and cache it. We will pass this component to the AlertsTable.
    const paginationComp = useMemo((): AlertsTableProps['paginationComp'] => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : alerts && alerts.range?.total,
                hasMoreItems    : (alerts && alerts.range
                    ? alerts.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!alerts || alerts.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage    : capitalizeAll(trans('label.paging.items_per_page', { item_name: trans('label.item', { count: 2 }) })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [searchParams.itemsPerPage, searchParams.currentPage, alerts, trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);



    return (
        <AlertsTable
            className={clsx(props.className, props.infoCompProps?.className)}
            style={props.style}
            title={props.title ?? capitalizeAll(trans('alerting.label.alert_other'))}
            icon={props.icon ?? 'notifications'}
            fieldsSettings={alertsTableFieldsSettings}
            listData={localizedAlertList}
            onListItemSelected={onAlertSelected}
            onPreviewItemClicked={onPreviewClicked}
            paginationComp={paginationComp}
            customSecondaryActions={props.customSecondaryActions}
            noDataMessageComp={(
                <GenericMessageBox
                    className={classes.noDataMessageComp}
                    title={capitalizeAll(trans('generic.no_data.title', { term: trans('alerting.label.alert', { count: 2 }) }))}
                />
            )}
            emptyDataMessageComp={(
                <GenericMessageBox
                    className={classes.noDataMessageComp}
                    title={capitalizeAll(trans('generic.no_data.title', { term: trans('alerting.label.alert', { count: 2 }) }))}
                />
            )}
        />
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('AlertsListingContainer', AlertsListingContainer);
